// The in-memory API's finance views (M5-04 fake twin of M5-03's routes, services/api/src/routes/
// finance/*, and M3-04's price lists and FX rates): the finance relations, categories, clients and
// their seat shares, revenue, compensation, recurring expenses, budget vs actual over the
// company → team → seat tree, margins per seat / team / client, the month's forecast, scenarios
// (FIN-01's own `runScenario`), the headcount plan and its diff against the chart, and CSV exports.
//
// The rules are the API's (services/api/src/domain/finance/access.ts):
//   - totals (budget vs actual, forecasts): managers and `finance_admin` see the whole tree; a seat
//     holder sees their seat, the seats under it and the teams it leads;
//   - restricted rows (compensation, benefits, revenue, margins, scenarios, the plan, exports):
//     `finance_admin` and the owner; `seat#comp_viewer` reads its seat's compensation and revenue;
//   - every finance write: `finance_admin` only (the owner too must grant it first); granting the
//     finance relations: the owner only;
//   - pay (compensation, benefits) is accrued on read, never posted to the ledger, and a seat shown
//     to someone who may not read its pay leaves it out (`restricted_hidden`); team and company
//     totals keep it.
// Simplifications, said once: hourly and contract pay accrues nothing here (the API prices
// approved time; the fake keeps time in another twin), the forecast is computed when read (the API
// stores a nightly snapshot), and exports are CSV only (XLSX is a 422; the API writes both).
// Every response parses with its contract (`fake-finance-contract.test.ts`).
import { rational, runScenario, type ScenarioChange } from "@companyos/domain-finance";
import { type OrgCalendarConfig, periodFromKey, periodKeyFor } from "@companyos/domain-kpi";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, page, problem, validation } from "./fake";
import { type FakeCompany, uid } from "./fake-data";
import type { BudgetRow, CostEvent, Seat, SeatAssignment } from "./types";

type Grant = OperationOutput<"financeAccess.list">["grants"][number];
type Category = OperationOutput<"categories.list">["data"][number];
type ClientDetail = OperationOutput<"clients.list">["data"][number];
type Revenue = OperationOutput<"revenueEvents.list">["data"][number];
type Comp = OperationOutput<"compensation.list">["data"][number];
type Recurring = OperationOutput<"recurringExpenses.list">["data"][number];
type Scenario = OperationOutput<"scenarios.list">["data"][number];
type ScenarioJson = Scenario["changes"][number];
type Plan = OperationOutput<"headcountPlan.list">["data"][number];
type Price = OperationOutput<"priceLists.list">["data"][number];
type Fx = OperationOutput<"fxRates.list">["data"][number];
type Node = OperationOutput<"finance.budgetVsActual">["nodes"][number];
type MarginRow = OperationOutput<"finance.margins">["rows"][number];
type Snapshot = OperationOutput<"forecasts.list">["rows"][number];
type Diff = OperationOutput<"headcountPlan.diff">;
type TeamRow = {
  id: string;
  name: string;
  parent_team_id: string | null;
  lead_seat_id: string | null;
};
type MemberRow = { team_id: string; seat_id: string; created_at: string };

export type FinanceDeps = {
  now: () => Date;
  /** The structure twin's seats and assignments (one store with Setup › Seats and the chart). */
  seatsOf: (company: FakeCompany) => Seat[];
  assignmentsOf: (company: FakeCompany) => SeatAssignment[];
  /** The teams twin's teams and members (one store with Setup › Teams). */
  teamsOf: (company: FakeCompany) => TeamRow[];
  teamMembersOf: (company: FakeCompany) => MemberRow[];
  roleName: (companyId: string, roleId: string) => string;
  /** Whether a membership is an active person of the caller's company. */
  personIn: (p: Principal, membershipId: string) => boolean;
  /** The cost ledger as the caller reads it (generated rows and posted ones). */
  ledgerOf: (p: Principal) => CostEvent[];
  /** The agents twin's budgets of a company. */
  budgetsOf: (companyId: string) => BudgetRow[];
};

/** Every route this twin serves (the contract test checks each one parses). */
export const FAKE_FINANCE_ROUTES: readonly RouteId[] = [
  "financeAccess.list",
  "financeAccess.grant",
  "financeAccess.revoke",
  "categories.list",
  "categories.create",
  "categories.update",
  "clients.list",
  "clients.create",
  "clients.update",
  "revenueEvents.list",
  "revenueEvents.create",
  "compensation.list",
  "compensation.create",
  "compensation.update",
  "recurringExpenses.list",
  "recurringExpenses.create",
  "recurringExpenses.update",
  "finance.budgetVsActual",
  "finance.margins",
  "finance.agentSpend",
  "forecasts.list",
  "scenarios.list",
  "scenarios.create",
  "scenarios.get",
  "scenarios.evaluate",
  "scenarios.update",
  "scenarios.delete",
  "headcountPlan.list",
  "headcountPlan.create",
  "headcountPlan.update",
  "headcountPlan.diff",
  "finance.export",
  "priceLists.list",
  "priceLists.create",
  "fxRates.list",
  "fxRates.put",
];

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DECIMAL = /^-?\d{1,12}(\.\d{1,6})?$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CURRENCIES = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "NZD",
  "CHF",
  "SGD",
  "PHP",
  "MXN",
  "INR",
  "ZAR",
] as const;
const PRICE_UNITS = [
  "token_in",
  "token_out",
  "cache_read",
  "sandbox_minute",
  "compute_minute",
  "tool_call",
  "storage_gb_month",
  "media_minute",
] as const;
const EPOCH = "2026-06-01T09:00:00.000Z";
const companyNo = (id: string) => Number.parseInt(id.slice(-4), 16) % 0x0fff;
const forbidden = (detail?: string) => problem(403, "forbidden", "Forbidden", detail);
const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);
const isCurrency = (v: unknown): v is (typeof CURRENCIES)[number] =>
  typeof v === "string" && (CURRENCIES as readonly string[]).includes(v);
const isInt = (v: unknown): v is number => typeof v === "number" && Number.isSafeInteger(v);
const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");
const optUuid = (v: unknown) =>
  v === undefined || v === null || (typeof v === "string" && UUID.test(v));
const monthIndex = (m: string) => Number(m.slice(0, 4)) * 12 + Number(m.slice(5, 7)) - 1;
const monthOf = (i: number) => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;

type Standing = {
  membershipId: string | null;
  manager: boolean;
  owner: boolean;
  financeAdmin: boolean;
  compSeats: Set<string>;
  held: string[];
};

type Line = {
  node: string;
  seatId: string | null;
  month: string;
  kind: string;
  minor: number;
  currency: string;
  restricted: boolean;
  /** A fixed amount (subscription, pay, recurring) rather than metered usage. */
  fixed: boolean;
  /** Computed on read (pay, recurring), never posted. */
  accrued: boolean;
};

type Store = {
  grants: Grant[];
  categories: Category[];
  clients: ClientDetail[];
  revenue: Revenue[];
  comp: Comp[];
  recurring: Recurring[];
  scenarios: Scenario[];
  plan: Plan[];
  prices: Price[];
  fx: Fx[];
};

export function createFinanceHandlers(d: FinanceDeps): Partial<Record<RouteId, Handler>> {
  const stores = new Map<string, Store>();
  let seq = 0;
  const stamp = () => d.now().toISOString();
  const next = (p: Principal, group: number) => uid(group, companyNo(p.company.id) * 4096 + ++seq);
  const actor = (p: Principal, a: Standing) => ({
    actor_kind: p.operator ? ("agency_user" as const) : ("person" as const),
    actor_membership_id: a.membershipId,
  });

  /** The company's records, seeded on first use with a little of everything. */
  function storeOf(company: FakeCompany): Store {
    const had = stores.get(company.id);
    if (had) return had;
    const n = companyNo(company.id);
    const id = (group: number, i: number) => uid(group, n * 64 + i);
    const sys = { actor_kind: "system" as const, actor_membership_id: null };
    const today = d.now();
    const month = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, "0")}`;
    const cur = company.reporting_currency;
    const seats = d.seatsOf(company);
    const de = d
      .assignmentsOf(company)
      .find((a) => a.membership_kind === "digital_employee" && a.active_to === null);
    const person = d
      .assignmentsOf(company)
      .find((a) => a.membership_kind === "person" && a.active_to === null);
    const supervisor = seats.find((s) => s.supervisor_seat_id === null) ?? seats[0];
    const s: Store = {
      grants: [],
      categories: [
        {
          company_id: company.id,
          id: id(0x8a00, 1),
          name: "AI services",
          kind: "expense",
          code: "6100",
          cost_kinds: ["de_subscription", "de_usage", "agent_tokens", "agent_compute"],
          status: "active",
          ...sys,
          created_at: EPOCH,
          updated_at: EPOCH,
        },
        {
          company_id: company.id,
          id: id(0x8a00, 2),
          name: "Placement fees",
          kind: "income",
          code: "4000",
          cost_kinds: [],
          status: "active",
          ...sys,
          created_at: EPOCH,
          updated_at: EPOCH,
        },
      ],
      clients: [],
      revenue: [],
      comp: [],
      recurring: [
        {
          company_id: company.id,
          id: id(0x8b00, 1),
          name: "Office and tools",
          kind: "overhead",
          amount_minor: 45_000,
          currency: cur,
          cadence: "monthly",
          seat_id: null,
          team_id: null,
          start_month: "2026-01",
          end_month: null,
          category_id: null,
          ...sys,
          created_at: EPOCH,
          updated_at: EPOCH,
        },
      ],
      scenarios: [],
      plan: [],
      prices: [
        price(company.id, id(0x8c00, 1), "platform", "token_in", "claude-sonnet", 3),
        price(company.id, id(0x8c00, 2), "platform", "token_out", "claude-sonnet", 15),
        price(company.id, id(0x8c00, 3), "platform", "sandbox_minute", null, 2_000),
      ],
      fx: [],
    };
    if (de) {
      s.clients.push({
        company_id: company.id,
        id: id(0x8d00, 1),
        name: "Northwind Staffing",
        status: "active",
        seats: [{ seat_id: de.seat_id, share: "1" }],
        ...sys,
        created_at: EPOCH,
        updated_at: EPOCH,
      });
      s.revenue.push({
        company_id: company.id,
        id: id(0x8e00, 1),
        occurred_at: `${month}-01T12:00:00.000Z`,
        period_key: month,
        amount_minor: 480_000,
        currency: cur,
        client_id: id(0x8d00, 1),
        seat_id: null,
        project_ref: null,
        kind: "invoice",
        category_id: id(0x8a00, 2),
        source_ref: { system: "invoice", id: `INV-${n}-001` },
        note: null,
        ...sys,
        created_at: EPOCH,
      });
    }
    if (supervisor) {
      s.comp.push({
        company_id: company.id,
        id: id(0x8f00, 1),
        seat_id: supervisor.id,
        membership_id: person?.seat_id === supervisor.id ? person.membership_id : null,
        amount_minor: 650_000,
        currency: cur,
        cadence: "monthly",
        effective_from: "2026-01-01",
        effective_to: null,
        category_id: null,
        note: null,
        ...sys,
        created_at: EPOCH,
        updated_at: EPOCH,
      });
      s.plan.push({
        company_id: company.id,
        id: id(0x8f80, 1),
        title: "Second sourcing DE",
        team_id: null,
        seat_id: null,
        holder_kind: "digital_employee",
        start_month: month,
        end_month: null,
        monthly_cost_minor: 90_000,
        currency: cur,
        status: "active",
        note: null,
        ...sys,
        created_at: EPOCH,
        updated_at: EPOCH,
      });
    }
    stores.set(company.id, s);
    return s;
  }

  function price(
    companyId: string,
    id: string,
    scope: Price["scope"],
    unit: Price["unit"],
    model: string | null,
    micros: number,
  ): Price {
    return {
      company_id: companyId,
      id,
      scope,
      source_price_id: null,
      unit,
      model,
      price_micros: micros,
      per_units: 1,
      tier_above_tokens: null,
      tier_price_micros: null,
      currency: "USD",
      effective_from: "2026-01-01",
      actor_kind: "system",
      actor_membership_id: null,
      created_at: EPOCH,
    };
  }

  // Access ---------------------------------------------------------------------------------------

  function standing(p: Principal): Standing {
    const membershipId =
      p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
    const grants = storeOf(p.company).grants.filter((g) => g.membership_id === membershipId);
    return {
      membershipId,
      manager: p.manager,
      owner: p.roles.includes("owner"),
      financeAdmin: membershipId !== null && grants.some((g) => g.relation === "finance_admin"),
      compSeats: new Set(
        grants.flatMap((g) => (g.relation === "comp_viewer" && g.seat_id ? [g.seat_id] : [])),
      ),
      held: membershipId
        ? d
            .assignmentsOf(p.company)
            .filter((x) => x.membership_id === membershipId && x.active_to === null)
            .map((x) => x.seat_id)
        : [],
    };
  }
  const allTotals = (a: Standing) => a.manager || a.financeAdmin;
  const allRestricted = (a: Standing) => a.financeAdmin || a.owner;
  const seesPay = (a: Standing, seatId: string | null) =>
    allRestricted(a) || (seatId !== null && a.compSeats.has(seatId));
  const writer = (a: Standing) =>
    a.financeAdmin ? null : forbidden("finance writes need finance_admin");
  const restrictedReader = (a: Standing) =>
    allRestricted(a) ? null : forbidden("finance_admin or the owner");

  /** The seats and teams whose totals the caller reads; null = everything; a 403 for nothing. */
  function totalsScope(
    p: Principal,
    a: Standing,
  ): { seats: Set<string>; teams: Set<string> } | null | Response {
    if (allTotals(a)) return null;
    if (!a.held.length) return forbidden("no seat and no finance standing");
    const all = d.seatsOf(p.company);
    const seats = new Set(a.held);
    for (let grew = true; grew; ) {
      grew = false;
      for (const s of all)
        if (s.supervisor_seat_id && seats.has(s.supervisor_seat_id) && !seats.has(s.id)) {
          seats.add(s.id);
          grew = true;
        }
    }
    const teamRows = d.teamsOf(p.company);
    const teams = new Set(
      teamRows.filter((t) => t.lead_seat_id && a.held.includes(t.lead_seat_id)).map((t) => t.id),
    );
    for (let grew = true; grew; ) {
      grew = false;
      for (const t of teamRows)
        if (t.parent_team_id && teams.has(t.parent_team_id) && !teams.has(t.id)) {
          teams.add(t.id);
          grew = true;
        }
    }
    return { seats, teams };
  }

  // The tree and its lines ------------------------------------------------------------------------

  /** Company → team (parent teams) → seat; a seat sits under its first team, else the company. */
  function treeOf(company: FakeCompany) {
    const teams = d.teamsOf(company);
    const members = d.teamMembersOf(company);
    const seats = d.seatsOf(company).map((seat) => {
      const first = members
        .filter((m) => m.seat_id === seat.id)
        .sort(
          (x, y) => x.created_at.localeCompare(y.created_at) || x.team_id.localeCompare(y.team_id),
        )[0];
      return { seat, teamId: first?.team_id ?? null };
    });
    const parent = new Map<string, string>();
    for (const t of teams) parent.set(t.id, t.parent_team_id ?? company.id);
    for (const s of seats) parent.set(s.seat.id, s.teamId ?? company.id);
    return { teams, seats, parent };
  }

  const calendarOf = (company: FakeCompany) => company.calendar_config as OrgCalendarConfig;

  /** The months of a month, quarter or year of the company calendar (422 for a bad key). */
  function monthsOf(company: FakeCompany, type: string, key: string): string[] | Response {
    if (type !== "month" && type !== "quarter" && type !== "year")
      return validation("period_type: month, quarter or year");
    const cal = calendarOf(company);
    let period: { startsAt: Date; endsAt: Date };
    try {
      period = periodFromKey(cal, type, key);
    } catch {
      return validation(`period_key: not a ${type} key`);
    }
    const out: string[] = [];
    for (let at = period.startsAt; at < period.endsAt; ) {
      const m = periodKeyFor(cal, "month", at);
      out.push(m);
      at = periodFromKey(cal, "month", m).endsAt;
    }
    return out;
  }

  /** Every line of `months`: the client ledger and the pay and recurring accruals. */
  function linesOf(p: Principal, months: readonly string[]): Line[] {
    const company = p.company;
    const s = storeOf(company);
    const { teams, seats } = treeOf(company);
    const seatIds = new Set(seats.map((x) => x.seat.id));
    const teamIds = new Set(teams.map((t) => t.id));
    const seatNode = (id: string | null) => (id && seatIds.has(id) ? id : company.id);
    const lines: Line[] = [];
    for (const c of s.comp) {
      if (c.cadence !== "monthly") continue; // hourly / contract: see the header
      for (const m of months) {
        if (c.effective_from.slice(0, 7) > m || (c.effective_to && c.effective_to.slice(0, 7) < m))
          continue;
        lines.push({
          node: seatNode(c.seat_id),
          seatId: c.seat_id,
          month: m,
          kind: "human_comp",
          minor: c.amount_minor,
          currency: c.currency,
          restricted: true,
          fixed: true,
          accrued: true,
        });
      }
    }
    const every = { monthly: 1, quarterly: 3, yearly: 12 } as const;
    for (const e of s.recurring)
      for (const m of months) {
        if (m < e.start_month || (e.end_month !== null && m > e.end_month)) continue;
        if ((monthIndex(m) - monthIndex(e.start_month)) % every[e.cadence] !== 0) continue;
        lines.push({
          node:
            e.seat_id !== null
              ? seatNode(e.seat_id)
              : e.team_id !== null && teamIds.has(e.team_id)
                ? e.team_id
                : company.id,
          seatId: e.seat_id,
          month: m,
          kind: e.kind,
          minor: e.amount_minor,
          currency: e.currency,
          restricted: e.kind === "benefit",
          fixed: true,
          accrued: true,
        });
      }
    for (const r of d.ledgerOf(p))
      if (r.visibility === "client" && months.includes(r.period_key) && r.amount_minor !== 0)
        lines.push({
          node: seatNode(r.seat_id),
          seatId: r.seat_id,
          month: r.period_key,
          kind: r.kind,
          minor: r.amount_minor,
          currency: r.currency,
          restricted: false,
          fixed: r.kind === "de_subscription",
          accrued: false,
        });
    return lines;
  }

  /** One amount in the reporting currency at its month's rate; a missing rate is a 422. */
  function toReporting(company: FakeCompany, minor: number, currency: string, month: string) {
    const to = company.reporting_currency;
    if (currency === to) return minor;
    const fx = storeOf(company).fx;
    const direct = fx.find(
      (r) => r.period_key === month && r.from_currency === currency && r.to_currency === to,
    );
    const inverse = fx.find(
      (r) => r.period_key === month && r.from_currency === to && r.to_currency === currency,
    );
    if (!direct && !inverse)
      throw new MissingRate(
        `no FX rate ${currency}→${to} for ${month}: set one in Setup › Finance › FX rates`,
      );
    const rate = direct
      ? rational.parseDecimal(direct.rate)
      : rational.div(rational.rat(1n), rational.parseDecimal((inverse as Fx).rate));
    return Number(rational.roundHalfEven(rational.mul(rational.rat(BigInt(minor)), rate)));
  }

  /** Own and subtree totals (and per kind) of `lines`, in the reporting currency. */
  function rollup(p: Principal, lines: readonly Line[]) {
    const { parent } = treeOf(p.company);
    const total = new Map<string, number>();
    const kinds = new Map<string, Map<string, number>>();
    for (const l of lines) {
      const v = toReporting(p.company, l.minor, l.currency, l.month);
      for (let at: string | undefined = l.node; at; at = parent.get(at)) {
        total.set(at, (total.get(at) ?? 0) + v);
        const k = kinds.get(at) ?? new Map<string, number>();
        k.set(l.kind, (k.get(l.kind) ?? 0) + v);
        kinds.set(at, k);
        if (at === p.company.id) break;
      }
    }
    return { total, kinds };
  }

  const decimal4 = (num: number, den: number) =>
    den === 0
      ? null
      : rational.toDecimal(rational.div(rational.rat(BigInt(num)), rational.rat(BigInt(den))), 4);

  /** Active budgets of the period at company / team / seat, in the reporting currency. */
  function budgetsAt(p: Principal, type: string, key: string, firstMonth: string) {
    const out = new Map<string, number>();
    for (const b of d.budgetsOf(p.company.id))
      if (
        b.state === "active" &&
        b.period_type === type &&
        b.period_key === key &&
        (b.subject_kind === "company" || b.subject_kind === "team" || b.subject_kind === "seat")
      )
        out.set(b.subject_id, toReporting(p.company, b.amount_minor, b.currency, firstMonth));
    return out;
  }

  function budgetVsActual(p: Principal, a: Standing, type: string, key: string) {
    const scope = totalsScope(p, a);
    if (scope instanceof Response) return scope;
    const months = monthsOf(p.company, type, key);
    if (months instanceof Response) return months;
    const lines = linesOf(p, months);
    const all = rollup(p, lines);
    const cut = rollup(
      p,
      lines.filter((l) => !l.restricted),
    );
    const budgets = budgetsAt(p, type, key, months[0] as string);
    const { teams, seats } = treeOf(p.company);
    const listed: Omit<
      Node,
      | "budget_minor"
      | "actual_minor"
      | "variance_minor"
      | "utilization"
      | "by_kind"
      | "restricted_hidden"
    >[] = [
      { kind: "company", id: p.company.id, name: p.company.name, parent_id: null },
      ...teams.map((t) => ({
        kind: "team" as const,
        id: t.id,
        name: t.name,
        parent_id: t.parent_team_id ?? p.company.id,
      })),
      ...seats.map((s) => ({
        kind: "seat" as const,
        id: s.seat.id,
        name: s.seat.title,
        parent_id: s.teamId ?? p.company.id,
      })),
    ];
    const nodes: Node[] = listed
      .filter(
        (n) =>
          scope === null ||
          (n.kind === "seat" && scope.seats.has(n.id)) ||
          (n.kind === "team" && scope.teams.has(n.id)),
      )
      .map((n) => {
        const hidden = n.kind === "seat" && !seesPay(a, n.id);
        const r = hidden ? cut : all;
        const actual = r.total.get(n.id) ?? 0;
        const budget = budgets.get(n.id) ?? null;
        const kinds = [...(r.kinds.get(n.id) ?? new Map<string, number>())].sort(([x], [y]) =>
          x.localeCompare(y),
        );
        return {
          ...n,
          budget_minor: budget,
          actual_minor: actual,
          variance_minor: budget === null ? null : budget - actual,
          utilization: budget === null ? null : decimal4(actual, budget),
          by_kind: Object.fromEntries(kinds),
          restricted_hidden: hidden,
        };
      });
    return json({
      period_type: type,
      period_key: key,
      currency: p.company.reporting_currency,
      nodes,
    });
  }

  function margins(p: Principal, type: string, key: string, by: string) {
    const months = monthsOf(p.company, type, key);
    if (months instanceof Response) return months;
    if (by !== "seat" && by !== "team" && by !== "client")
      return validation("by: seat, team or client");
    const s = storeOf(p.company);
    const costs = rollup(p, linesOf(p, months)).total;
    const revenue = s.revenue
      .filter((r) => months.includes(r.period_key))
      .map((r) => ({ ...r, v: toReporting(p.company, r.amount_minor, r.currency, r.period_key) }));
    const { teams, seats, parent } = treeOf(p.company);
    const seatRevenue = new Map<string, number>();
    for (const r of revenue) {
      if (r.seat_id) seatRevenue.set(r.seat_id, (seatRevenue.get(r.seat_id) ?? 0) + r.v);
      else if (r.client_id)
        for (const cs of s.clients.find((c) => c.id === r.client_id)?.seats ?? [])
          seatRevenue.set(
            cs.seat_id,
            (seatRevenue.get(cs.seat_id) ?? 0) +
              Number(
                rational.roundHalfEven(
                  rational.mul(rational.rat(BigInt(r.v)), rational.parseDecimal(cs.share)),
                ),
              ),
          );
    }
    const row = (
      kind: MarginRow["subject_kind"],
      id: string,
      name: string,
      cost: number,
      rev: number,
    ): MarginRow => ({
      subject_kind: kind,
      subject_id: id,
      name,
      cost_minor: cost,
      revenue_minor: rev,
      margin_minor: rev - cost,
      margin_ratio: decimal4(rev - cost, rev),
    });
    const under = (seatId: string, teamId: string) => {
      for (let at = parent.get(seatId); at; at = parent.get(at)) {
        if (at === teamId) return true;
        if (at === p.company.id) return false;
      }
      return false;
    };
    const rows: MarginRow[] =
      by === "seat"
        ? seats.map((x) =>
            row(
              "seat",
              x.seat.id,
              x.seat.title,
              costs.get(x.seat.id) ?? 0,
              seatRevenue.get(x.seat.id) ?? 0,
            ),
          )
        : by === "team"
          ? teams.map((t) =>
              row(
                "team",
                t.id,
                t.name,
                costs.get(t.id) ?? 0,
                seats
                  .filter((x) => under(x.seat.id, t.id))
                  .reduce((n, x) => n + (seatRevenue.get(x.seat.id) ?? 0), 0),
              ),
            )
          : s.clients.map((c) =>
              row(
                "client",
                c.id,
                c.name,
                c.seats.reduce(
                  (n, cs) =>
                    n +
                    Number(
                      rational.roundHalfEven(
                        rational.mul(
                          rational.rat(BigInt(costs.get(cs.seat_id) ?? 0)),
                          rational.parseDecimal(cs.share),
                        ),
                      ),
                    ),
                  0,
                ),
                revenue.filter((r) => r.client_id === c.id).reduce((n, r) => n + r.v, 0),
              ),
            );
    return json({
      period_type: type,
      period_key: key,
      by,
      currency: p.company.reporting_currency,
      rows,
      total: row(
        "company",
        p.company.id,
        p.company.name,
        costs.get(p.company.id) ?? 0,
        revenue.reduce((n, r) => n + r.v, 0),
      ),
    });
  }

  /** The month's run-rate forecast per node, as of today (the API stores it nightly). */
  function forecast(p: Principal, a: Standing, month: string) {
    const scope = totalsScope(p, a);
    if (scope instanceof Response) return scope;
    const now = d.now();
    const current = periodKeyFor(calendarOf(p.company), "month", now);
    if (month > current) return json({ period_key: month, as_of: null, rows: [] });
    const period = periodFromKey(calendarOf(p.company), "month", month);
    const elapsed = Math.min(
      1,
      (now.getTime() - period.startsAt.getTime()) /
        (period.endsAt.getTime() - period.startsAt.getTime()),
    );
    const asOf =
      month === current
        ? now.toISOString().slice(0, 10)
        : new Date(period.endsAt.getTime() - 1).toISOString().slice(0, 10);
    const lines = linesOf(p, [month]);
    const variable = rollup(
      p,
      lines.filter((l) => !l.fixed),
    ).total;
    const posted = rollup(
      p,
      lines.filter((l) => l.fixed && !l.accrued),
    ).total;
    const committed = rollup(
      p,
      lines.filter((l) => l.accrued),
    ).total;
    const budgets = budgetsAt(p, "month", month, month);
    const { teams, seats } = treeOf(p.company);
    const subjects: [Snapshot["subject_kind"], string][] = [
      ["company", p.company.id],
      ...teams.map((t) => ["team", t.id] as [Snapshot["subject_kind"], string]),
      ...seats.map((s) => ["seat", s.seat.id] as [Snapshot["subject_kind"], string]),
    ];
    const rows: Snapshot[] = subjects
      .filter(
        ([kind, id]) =>
          scope === null ||
          (kind === "seat" && scope.seats.has(id)) ||
          (kind === "team" && scope.teams.has(id)),
      )
      .map(([kind, id], i) => {
        const v = variable.get(id) ?? 0;
        const runRate = elapsed > 0 ? Math.round(v / elapsed) : null;
        const fixed = posted.get(id) ?? 0;
        const due = committed.get(id) ?? 0;
        return {
          company_id: p.company.id,
          id: uid(0x8f40, companyNo(p.company.id) * 4096 + i),
          subject_kind: kind,
          subject_id: id,
          period_key: month,
          as_of: asOf,
          actual_minor: v + fixed,
          run_rate_minor: runRate,
          committed_minor: due,
          projected_minor: (runRate ?? v) + fixed + due,
          budget_minor: budgets.get(id) ?? null,
          currency: p.company.reporting_currency,
          created_at: `${asOf}T02:00:00.000Z`,
        };
      });
    return json({ period_key: month, as_of: asOf, rows });
  }

  /** FIN-01's `runScenario` over today's tree for the base month (the API's baseline). */
  function evaluate(p: Principal, sc: Scenario): Scenario | Response {
    const lines = linesOf(p, [sc.base_period]);
    const costs = rollup(p, lines).total;
    const revenue = storeOf(p.company)
      .revenue.filter((r) => r.period_key === sc.base_period)
      .reduce((n, r) => n + toReporting(p.company, r.amount_minor, r.currency, r.period_key), 0);
    const holders = d.assignmentsOf(p.company).filter((x) => x.active_to === null);
    const seatLines = treeOf(p.company)
      .seats.filter((s) => holders.some((h) => h.seat_id === s.seat.id))
      .map((s) => ({
        seatTemplate: d.roleName(p.company.id, s.seat.role_id),
        holderKind: holders.some((h) => h.seat_id === s.seat.id && h.membership_kind === "person")
          ? ("person" as const)
          : ("digital_employee" as const),
        count: 1,
        costPerSeatMinor: BigInt(costs.get(s.seat.id) ?? 0),
      }));
    const total = BigInt(costs.get(p.company.id) ?? 0);
    const seatCost = seatLines.reduce((n, l) => n + l.costPerSeatMinor, 0n);
    const budgets = Object.fromEntries(
      [...budgetsAt(p, "month", sc.base_period, sc.base_period)].map(([k, v]) => [k, BigInt(v)]),
    );
    let out: ReturnType<typeof runScenario>;
    try {
      out = runScenario(
        {
          basePeriodKey: sc.base_period,
          revenueMinor: BigInt(revenue),
          otherCostMinor: total - seatCost,
          seats: seatLines,
          budgets,
          kpis: [],
        },
        sc.changes.map(toEngine),
      );
    } catch (e) {
      if (e instanceof Error && e.name === "ScenarioError")
        return validation(`changes: ${e.message}`);
      throw e;
    }
    const at = stamp();
    return {
      ...sc,
      result: {
        base_period: out.basePeriodKey,
        currency: p.company.reporting_currency,
        cost: {
          base_minor: Number(out.cost.baseMinor),
          scenario_minor: Number(out.cost.scenarioMinor),
          delta_minor: Number(out.cost.deltaMinor),
        },
        revenue_minor: Number(out.revenueMinor),
        margin: {
          base_minor: Number(out.margin.baseMinor),
          scenario_minor: Number(out.margin.scenarioMinor),
          scenario_ratio:
            out.margin.scenarioRatio === null
              ? null
              : rational.toDecimal(out.margin.scenarioRatio, 4),
        },
        seats: out.seats.map((x) => ({
          seat_template: x.seatTemplate,
          holder_kind: x.holderKind,
          count: x.count,
          cost_per_seat_minor: Number(x.costPerSeatMinor),
        })),
        budgets: Object.fromEntries(Object.entries(out.budgets).map(([k, v]) => [k, Number(v)])),
        kpis: [],
      },
      evaluated_at: at,
      updated_at: at,
    };
  }

  function headcountDiff(p: Principal, month: string): Diff {
    const s = storeOf(p.company);
    const lines = s.plan.filter(
      (l) =>
        l.status === "active" &&
        l.start_month <= month &&
        (l.end_month === null || l.end_month >= month),
    );
    const holders = d.assignmentsOf(p.company).filter((x) => x.active_to === null);
    const kindsOn = (seatId: string) =>
      new Set(holders.filter((h) => h.seat_id === seatId).map((h) => h.membership_kind));
    const { teams, seats } = treeOf(p.company);
    // Buckets per team (a seat's first team; null = the company), as the API's `headcountDiff`.
    type Counts = { person: number; digital_employee: number };
    const buckets = new Map<string | null, { planned: Counts; live: Counts & { open: number } }>();
    const bucket = (id: string | null) => {
      let b = buckets.get(id);
      if (!b) {
        b = {
          planned: { person: 0, digital_employee: 0 },
          live: { person: 0, digital_employee: 0, open: 0 },
        };
        buckets.set(id, b);
      }
      return b;
    };
    for (const x of seats) {
      if (x.seat.status === "closed" || x.seat.status === "proposed") continue;
      const b = bucket(x.teamId);
      const k = kindsOn(x.seat.id);
      if (k.has("person")) b.live.person += 1;
      else if (k.has("digital_employee")) b.live.digital_employee += 1;
      else b.live.open += 1;
    }
    const out = lines.map((l) => {
      const seat = l.seat_id ? seats.find((x) => x.seat.id === l.seat_id) : undefined;
      bucket(l.team_id ?? seat?.teamId ?? null).planned[l.holder_kind] += 1;
      const k = seat ? kindsOn(seat.seat.id) : null;
      return {
        id: l.id,
        title: l.title,
        seat_id: l.seat_id,
        status: !k
          ? ("planned" as const)
          : k.size === 0
            ? ("unfilled" as const)
            : k.has(l.holder_kind)
              ? ("matched" as const)
              : ("holder_mismatch" as const),
      };
    });
    return {
      month,
      currency: p.company.reporting_currency,
      planned_cost_minor: lines.reduce(
        (n, l) => n + toReporting(p.company, l.monthly_cost_minor, l.currency, month),
        0,
      ),
      teams: [...buckets]
        .sort(([x], [y]) => (x ?? "").localeCompare(y ?? ""))
        .map(([id, b]) => ({
          team_id: id,
          name: id === null ? p.company.name : (teams.find((t) => t.id === id)?.name ?? ""),
          planned: b.planned,
          live: b.live,
          delta: {
            person: b.planned.person - b.live.person,
            digital_employee: b.planned.digital_employee - b.live.digital_employee,
          },
        })),
      lines: out,
    };
  }

  /** The export as CSV (minor units and an exact decimal, the category of each row). */
  function exportCsv(p: Principal, kind: string, from: string, to: string): string | Response {
    if (!MONTH.test(from) || !MONTH.test(to))
      return validation("period_from / period_to: month keys");
    if (from > to) return validation("period_to: before period_from");
    if (monthIndex(to) - monthIndex(from) >= 36) return validation("period_to: at most 36 months");
    const months = Array.from({ length: monthIndex(to) - monthIndex(from) + 1 }, (_, i) =>
      monthOf(monthIndex(from) + i),
    );
    const s = storeOf(p.company);
    const expense = s.categories.filter((c) => c.status === "active" && c.kind === "expense");
    const catOfKind = (k: string) =>
      expense
        .filter((c) => c.cost_kinds.includes(k))
        .sort((x, y) => (x.code ?? "~").localeCompare(y.code ?? "~"))[0];
    const major = (minor: number) => {
      const abs = Math.abs(minor);
      return `${minor < 0 ? "-" : ""}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
    };
    const cell = (v: unknown) => {
      const t = v === null || v === undefined ? "" : String(v);
      return /[",\n\r]/.test(t) ? `"${t.replaceAll('"', '""')}"` : t;
    };
    let table: unknown[][];
    if (kind === "cost_events") {
      table = [
        [
          "id",
          "occurred_at",
          "period_key",
          "kind",
          "seat_id",
          "amount_minor",
          "amount",
          "currency",
          "category_code",
          "category_name",
        ],
        ...d
          .ledgerOf(p)
          .filter((r) => r.visibility === "client" && months.includes(r.period_key))
          .sort((x, y) => x.occurred_at.localeCompare(y.occurred_at) || x.id.localeCompare(y.id))
          .map((r) => {
            const c = catOfKind(r.kind);
            return [
              r.id,
              r.occurred_at,
              r.period_key,
              r.kind,
              r.seat_id,
              r.amount_minor,
              major(r.amount_minor),
              r.currency,
              c?.code,
              c?.name,
            ];
          }),
      ];
    } else if (kind === "revenue_events") {
      table = [
        [
          "id",
          "occurred_at",
          "period_key",
          "kind",
          "client_id",
          "seat_id",
          "amount_minor",
          "amount",
          "currency",
          "category_code",
          "category_name",
        ],
        ...s.revenue
          .filter((r) => months.includes(r.period_key))
          .map((r) => {
            const c = s.categories.find((x) => x.id === r.category_id);
            return [
              r.id,
              r.occurred_at,
              r.period_key,
              r.kind,
              r.client_id,
              r.seat_id,
              r.amount_minor,
              major(r.amount_minor),
              r.currency,
              c?.code,
              c?.name,
            ];
          }),
      ];
    } else if (kind === "rollup") {
      const rows = new Map<string, number>();
      for (const l of linesOf(p, months)) {
        const k = `${l.month}|${l.node}|${l.kind}`;
        rows.set(k, (rows.get(k) ?? 0) + toReporting(p.company, l.minor, l.currency, l.month));
      }
      table = [
        [
          "period_key",
          "node_id",
          "kind",
          "amount_minor",
          "amount",
          "currency",
          "category_code",
          "category_name",
        ],
        ...[...rows]
          .sort(([x], [y]) => x.localeCompare(y))
          .map(([k, v]) => {
            const [month, node, kindOf] = k.split("|") as [string, string, string];
            const c = catOfKind(kindOf);
            return [
              month,
              node,
              kindOf,
              v,
              major(v),
              p.company.reporting_currency,
              c?.code,
              c?.name,
            ];
          }),
      ];
    } else return validation("kind: cost_events, revenue_events or rollup");
    return `${table.map((r) => r.map(cell).join(",")).join("\r\n")}\r\n`;
  }

  // Validation of bodies ----------------------------------------------------------------------------

  const seatIn = (p: Principal, id: unknown) =>
    typeof id === "string" && d.seatsOf(p.company).some((s) => s.id === id);
  const teamIn = (p: Principal, id: unknown) =>
    typeof id === "string" && d.teamsOf(p.company).some((t) => t.id === id);
  const categoryIn = (p: Principal, id: unknown) =>
    id === undefined || id === null || storeOf(p.company).categories.some((c) => c.id === id);

  function sharesError(p: Principal, seats: unknown, clientId: string | null): string | null {
    if (seats === undefined) return null;
    if (!Array.isArray(seats)) return "seats: a list";
    for (const x of seats as { seat_id?: unknown; share?: unknown }[]) {
      if (!seatIn(p, x.seat_id)) return "seats: unknown seat";
      const share = typeof x.share === "number" ? String(x.share) : x.share;
      if (typeof share !== "string" || !DECIMAL.test(share)) return "seats: share is a decimal";
      const r = rational.parseDecimal(share);
      if (rational.cmp(r, rational.rat(0n)) <= 0 || rational.cmp(r, rational.rat(1n)) > 0)
        return "seats: a share is above 0 and at most 1";
    }
    // The shares of a seat over all clients sum to at most 1.
    const others = storeOf(p.company).clients.filter(
      (c) => c.id !== clientId && c.status === "active",
    );
    for (const x of seats as { seat_id: string; share: string | number }[]) {
      const sum = [...others.flatMap((c) => c.seats), x]
        .filter((y) => y.seat_id === x.seat_id)
        .reduce(
          (n, y) => rational.add(n, rational.parseDecimal(String(y.share))),
          rational.rat(0n),
        );
      if (rational.cmp(sum, rational.rat(1n)) > 0)
        return "seats: a seat's shares over every client sum to more than 1";
    }
    return null;
  }
  const shares = (seats: unknown) =>
    ((seats ?? []) as { seat_id: string; share: string | number }[]).map((x) => ({
      seat_id: x.seat_id,
      share:
        rational.toDecimal(rational.parseDecimal(String(x.share)), 4).replace(/\.?0+$/, "") || "0",
    }));

  // Handlers ----------------------------------------------------------------------------------------

  /** Runs `fn` as the caller's standing; a missing FX rate anywhere is a 422. */
  const as =
    (fn: (p: Principal, a: Standing, ctx: Parameters<Handler>[0]) => Response): Handler =>
    (ctx) => {
      if (!ctx.principal) return notFound();
      try {
        return fn(ctx.principal, standing(ctx.principal), ctx);
      } catch (e) {
        if (e instanceof MissingRate) return validation(e.message);
        throw e;
      }
    };

  const list = <T extends { company_id: string }>(rows: T[], url: URL) => json(page(rows, url));

  return {
    // Finance relations -----------------------------------------------------------------------------
    "financeAccess.list": as((p, a) =>
      json({
        grants:
          a.owner || a.manager || a.financeAdmin
            ? [...storeOf(p.company).grants].sort(
                (x, y) =>
                  x.relation.localeCompare(y.relation) ||
                  x.membership_id.localeCompare(y.membership_id) ||
                  (x.seat_id ?? "").localeCompare(y.seat_id ?? ""),
              )
            : [],
        me: {
          finance_admin: a.financeAdmin,
          owner: a.owner,
          comp_viewer_seat_ids: [...a.compSeats].sort(),
          reads_totals: allTotals(a) || a.held.length > 0,
          reads_restricted: allRestricted(a),
        },
      }),
    ),
    "financeAccess.grant": as((p, a, { body }) => {
      if (!a.owner) return forbidden("only the owner grants finance access");
      const g = grantOf(p, body);
      if (g instanceof Response) return g;
      const s = storeOf(p.company);
      if (!s.grants.some((x) => same(x, g))) s.grants.push(g);
      return json(g, 201);
    }),
    "financeAccess.revoke": as((p, a, { body }) => {
      if (!a.owner) return forbidden("only the owner revokes finance access");
      const g = grantOf(p, body);
      if (g instanceof Response) return g;
      const s = storeOf(p.company);
      s.grants = s.grants.filter((x) => !same(x, g));
      return json(g);
    }),

    // Categories -------------------------------------------------------------------------------------
    "categories.list": as((p, a, { url }) =>
      a.manager || a.financeAdmin || a.owner
        ? list(storeOf(p.company).categories, url)
        : forbidden(),
    ),
    "categories.create": as((p, a, { body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const b = (body ?? {}) as Record<string, unknown>;
      if (!str(b.name)) return validation("name: required");
      if (b.kind !== "income" && b.kind !== "expense") return validation("kind: income or expense");
      const s = storeOf(p.company);
      const code = b.code ? str(b.code) : null;
      if (code && s.categories.some((c) => c.code === code))
        return conflict("a category has this code");
      const at = stamp();
      const row: Category = {
        company_id: p.company.id,
        id: next(p, 0x8a00),
        name: str(b.name),
        kind: b.kind,
        code,
        cost_kinds: Array.isArray(b.cost_kinds) ? (b.cost_kinds as string[]) : [],
        status: "active",
        ...actor(p, a),
        created_at: at,
        updated_at: at,
      };
      s.categories.push(row);
      return json(row, 201);
    }),
    "categories.update": as((p, a, { params, body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const s = storeOf(p.company);
      const row = s.categories.find((c) => c.id === params.id);
      if (!row) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.code && s.categories.some((c) => c.id !== row.id && c.code === str(b.code)))
        return conflict("a category has this code");
      if (b.name !== undefined) row.name = str(b.name);
      if (b.code !== undefined) row.code = b.code ? str(b.code) : null;
      if (Array.isArray(b.cost_kinds)) row.cost_kinds = b.cost_kinds as string[];
      if (b.status === "active" || b.status === "archived") row.status = b.status;
      row.updated_at = stamp();
      return json(row);
    }),

    // Clients -----------------------------------------------------------------------------------------
    "clients.list": as((p, a, { url }) =>
      a.manager || a.financeAdmin || a.owner ? list(storeOf(p.company).clients, url) : forbidden(),
    ),
    "clients.create": as((p, a, { body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const b = (body ?? {}) as Record<string, unknown>;
      if (!str(b.name)) return validation("name: required");
      const bad = sharesError(p, b.seats, null);
      if (bad) return validation(bad);
      const s = storeOf(p.company);
      if (s.clients.some((c) => c.name.toLowerCase() === str(b.name).toLowerCase()))
        return conflict("a client has this name");
      const at = stamp();
      const row: ClientDetail = {
        company_id: p.company.id,
        id: next(p, 0x8d00),
        name: str(b.name),
        status: "active",
        seats: shares(b.seats),
        ...actor(p, a),
        created_at: at,
        updated_at: at,
      };
      s.clients.push(row);
      return json(row, 201);
    }),
    "clients.update": as((p, a, { params, body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const s = storeOf(p.company);
      const row = s.clients.find((c) => c.id === params.id);
      if (!row) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const bad = sharesError(p, b.seats, row.id);
      if (bad) return validation(bad);
      if (b.name !== undefined) {
        if (!str(b.name)) return validation("name: required");
        if (
          s.clients.some(
            (c) => c.id !== row.id && c.name.toLowerCase() === str(b.name).toLowerCase(),
          )
        )
          return conflict("a client has this name");
        row.name = str(b.name);
      }
      if (b.status === "active" || b.status === "archived") row.status = b.status;
      if (b.seats !== undefined) row.seats = shares(b.seats);
      row.updated_at = stamp();
      return json(row);
    }),

    // Revenue ------------------------------------------------------------------------------------------
    "revenueEvents.list": as((p, a, { url }) => {
      if (!allRestricted(a) && a.compSeats.size === 0) return forbidden();
      const q = url.searchParams;
      const rows = storeOf(p.company)
        .revenue.filter(
          (r) =>
            (allRestricted(a) || (r.seat_id !== null && a.compSeats.has(r.seat_id))) &&
            (!q.get("period_key") || r.period_key === q.get("period_key")) &&
            (!q.get("client_id") || r.client_id === q.get("client_id")) &&
            (!q.get("seat_id") || r.seat_id === q.get("seat_id")),
        )
        .sort((x, y) => y.occurred_at.localeCompare(x.occurred_at) || y.id.localeCompare(x.id));
      return list(rows, url);
    }),
    "revenueEvents.create": as((p, a, { body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const b = (body ?? {}) as Record<string, unknown>;
      if (!isInt(b.amount_minor) || b.amount_minor === 0)
        return validation("amount_minor: a revenue row is never zero");
      if (!isCurrency(b.currency)) return validation("currency");
      if (b.kind !== "invoice" && b.kind !== "subscription" && b.kind !== "manual")
        return validation("kind");
      const occurred = Date.parse(String(b.occurred_at));
      if (!Number.isFinite(occurred)) return validation("occurred_at: a timestamp");
      const s = storeOf(p.company);
      if (b.client_id && !s.clients.some((c) => c.id === b.client_id))
        return validation("client_id: unknown client");
      if (b.seat_id && !seatIn(p, b.seat_id)) return validation("seat_id: unknown seat");
      if (!categoryIn(p, b.category_id)) return validation("category_id: unknown category");
      const at = stamp();
      const when = new Date(occurred).toISOString();
      const row: Revenue = {
        company_id: p.company.id,
        id: next(p, 0x8e00),
        occurred_at: when,
        period_key: periodKeyFor(calendarOf(p.company), "month", new Date(occurred)),
        amount_minor: b.amount_minor,
        currency: b.currency,
        client_id: (b.client_id as string | null | undefined) ?? null,
        seat_id: (b.seat_id as string | null | undefined) ?? null,
        project_ref: b.project_ref ? str(b.project_ref) : null,
        kind: b.kind,
        category_id: (b.category_id as string | null | undefined) ?? null,
        source_ref: (b.source_ref as Revenue["source_ref"]) ?? null,
        note: b.note ? String(b.note) : null,
        ...actor(p, a),
        created_at: at,
      };
      s.revenue.push(row);
      return json(row, 201);
    }),

    // Compensation -------------------------------------------------------------------------------------
    "compensation.list": as((p, a, { url }) => {
      if (!allRestricted(a) && a.compSeats.size === 0) return forbidden();
      const seat = url.searchParams.get("seat_id");
      const rows = storeOf(p.company).comp.filter(
        (c) => seesPay(a, c.seat_id) && (!seat || c.seat_id === seat),
      );
      return list(rows, url);
    }),
    "compensation.create": as((p, a, { body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const b = (body ?? {}) as Record<string, unknown>;
      const bad = compError(p, b, true);
      if (bad) return validation(bad);
      const at = stamp();
      const row: Comp = {
        company_id: p.company.id,
        id: next(p, 0x8f00),
        seat_id: b.seat_id as string,
        membership_id: (b.membership_id as string | null | undefined) ?? null,
        amount_minor: b.amount_minor as number,
        currency: b.currency as Comp["currency"],
        cadence: b.cadence as Comp["cadence"],
        effective_from: b.effective_from as string,
        effective_to: (b.effective_to as string | null | undefined) ?? null,
        category_id: (b.category_id as string | null | undefined) ?? null,
        note: b.note ? String(b.note) : null,
        ...actor(p, a),
        created_at: at,
        updated_at: at,
      };
      storeOf(p.company).comp.push(row);
      return json(row, 201);
    }),
    "compensation.update": as((p, a, { params, body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const row = storeOf(p.company).comp.find((c) => c.id === params.id);
      if (!row) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const merged = { ...row, ...b };
      const bad = compError(p, merged, false);
      if (bad) return validation(bad);
      Object.assign(row, {
        membership_id: merged.membership_id ?? null,
        amount_minor: merged.amount_minor,
        currency: merged.currency,
        effective_from: merged.effective_from,
        effective_to: merged.effective_to ?? null,
        category_id: merged.category_id ?? null,
        note: merged.note ?? null,
        updated_at: stamp(),
      });
      return json(row);
    }),

    // Recurring expenses ---------------------------------------------------------------------------------
    "recurringExpenses.list": as((p, a, { url }) => {
      if (!a.manager && !a.financeAdmin && !a.owner && a.compSeats.size === 0) return forbidden();
      const kind = url.searchParams.get("kind");
      const rows = storeOf(p.company).recurring.filter(
        (e) =>
          (e.kind !== "benefit" || seesPay(a, e.seat_id)) &&
          (allTotals(a) || a.owner || e.kind === "benefit") &&
          (!kind || e.kind === kind),
      );
      return list(rows, url);
    }),
    "recurringExpenses.create": as((p, a, { body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const b = (body ?? {}) as Record<string, unknown>;
      if (!["benefit", "overhead", "software_subscription"].includes(String(b.kind)))
        return validation("kind");
      const bad = recurringError(p, b);
      if (bad) return validation(bad);
      const at = stamp();
      const row: Recurring = {
        company_id: p.company.id,
        id: next(p, 0x8b00),
        name: str(b.name),
        kind: b.kind as Recurring["kind"],
        amount_minor: b.amount_minor as number,
        currency: b.currency as Recurring["currency"],
        cadence: (b.cadence as Recurring["cadence"] | undefined) ?? "monthly",
        seat_id: (b.seat_id as string | null | undefined) ?? null,
        team_id: (b.team_id as string | null | undefined) ?? null,
        start_month: b.start_month as string,
        end_month: (b.end_month as string | null | undefined) ?? null,
        category_id: (b.category_id as string | null | undefined) ?? null,
        ...actor(p, a),
        created_at: at,
        updated_at: at,
      };
      storeOf(p.company).recurring.push(row);
      return json(row, 201);
    }),
    "recurringExpenses.update": as((p, a, { params, body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const row = storeOf(p.company).recurring.find((e) => e.id === params.id);
      if (!row) return notFound();
      const merged = { ...row, ...((body ?? {}) as Record<string, unknown>) };
      const bad = recurringError(p, merged);
      if (bad) return validation(bad);
      Object.assign(row, {
        name: str(merged.name),
        amount_minor: merged.amount_minor,
        currency: merged.currency,
        cadence: merged.cadence ?? "monthly",
        seat_id: merged.seat_id ?? null,
        team_id: merged.team_id ?? null,
        start_month: merged.start_month,
        end_month: merged.end_month ?? null,
        category_id: merged.category_id ?? null,
        updated_at: stamp(),
      });
      return json(row);
    }),

    // Views -------------------------------------------------------------------------------------------
    "finance.budgetVsActual": as((p, a, { url }) =>
      budgetVsActual(
        p,
        a,
        url.searchParams.get("period_type") ?? "month",
        url.searchParams.get("period_key") ?? "",
      ),
    ),
    "finance.margins": as((p, a, { url }) => {
      const refused = restrictedReader(a);
      if (refused) return refused;
      return margins(
        p,
        url.searchParams.get("period_type") ?? "month",
        url.searchParams.get("period_key") ?? "",
        url.searchParams.get("by") ?? "",
      );
    }),
    // SPEND-KPIS: the twin has no `spend.snapshot` job, so the view is honestly empty (never
    // summed: no rows, zero totals, a zero 30-day trend), scoped like budget vs actual.
    "finance.agentSpend": as((p, a, { url }) => {
      const scope = totalsScope(p, a);
      if (scope instanceof Response) return scope;
      const now = d.now();
      const cal = calendarOf(p.company);
      const month = url.searchParams.get("period_key") ?? periodKeyFor(cal, "month", now);
      if (!MONTH.test(month)) return validation("period_key: a month key");
      const day = 86_400_000;
      return json({
        period_key: month,
        week_key: periodKeyFor(cal, "week", now),
        today: now.toISOString().slice(0, 10),
        currency: p.company.reporting_currency,
        computed_at: null,
        totals: {
          today_minor: 0,
          week_minor: 0,
          period_minor: 0,
          avg_day_minor: 0,
          avg_week_minor: 0,
          tasks_done: 0,
          per_task_minor: null,
        },
        rows: [],
        by_tier: [],
        trend: Array.from({ length: 30 }, (_, i) => ({
          day: new Date(now.getTime() - (29 - i) * day).toISOString().slice(0, 10),
          amount_minor: 0,
        })),
      });
    }),
    "forecasts.list": as((p, a, { url }) => {
      const month =
        url.searchParams.get("period_key") ?? periodKeyFor(calendarOf(p.company), "month", d.now());
      if (!MONTH.test(month)) return validation("period_key: a month key");
      return forecast(p, a, month);
    }),

    // Scenarios ---------------------------------------------------------------------------------------
    "scenarios.list": as((p, a, { url }) => {
      const refused = restrictedReader(a);
      if (refused) return refused;
      return list(
        [...storeOf(p.company).scenarios].sort((x, y) => x.id.localeCompare(y.id)),
        url,
      );
    }),
    "scenarios.get": as((p, a, { params }) => {
      const refused = restrictedReader(a);
      if (refused) return refused;
      const row = storeOf(p.company).scenarios.find((x) => x.id === params.id);
      return row ? json(row) : notFound();
    }),
    "scenarios.create": as((p, a, { body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const b = (body ?? {}) as Record<string, unknown>;
      if (!str(b.name)) return validation("name: required");
      if (typeof b.base_period !== "string" || !MONTH.test(b.base_period))
        return validation("base_period: a month key");
      if (!Array.isArray(b.changes) || b.changes.length < 1 || b.changes.length > 50)
        return validation("changes: 1 to 50");
      const bad = (b.changes as Record<string, unknown>[])
        .map((c) => changeError(p, c))
        .find(Boolean);
      if (bad) return validation(bad);
      const at = stamp();
      const row: Scenario = {
        company_id: p.company.id,
        id: next(p, 0x8f20),
        name: str(b.name),
        base_period: b.base_period,
        changes: b.changes as ScenarioJson[],
        result: null,
        evaluated_at: null,
        ...actor(p, a),
        created_at: at,
        updated_at: at,
      };
      const evaluated = evaluate(p, row);
      if (evaluated instanceof Response) return evaluated;
      storeOf(p.company).scenarios.push(evaluated);
      return json(evaluated, 201);
    }),
    "scenarios.evaluate": as((p, a, { params }) => {
      const refused = writer(a);
      if (refused) return refused;
      const s = storeOf(p.company);
      const i = s.scenarios.findIndex((x) => x.id === params.id);
      const row = s.scenarios[i];
      if (!row) return notFound();
      const evaluated = evaluate(p, row);
      if (evaluated instanceof Response) return evaluated;
      s.scenarios[i] = evaluated;
      return json(evaluated);
    }),
    // ACTIONS: edit (re-evaluated) and delete.
    "scenarios.update": as((p, a, { params, body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const s = storeOf(p.company);
      const i = s.scenarios.findIndex((x) => x.id === params.id);
      const row = s.scenarios[i];
      if (!row) return notFound();
      const b = (body ?? {}) as Partial<Pick<Scenario, "name" | "base_period" | "changes">>;
      if (b.name === undefined && b.base_period === undefined && b.changes === undefined)
        return validation("at least one field");
      if (b.name !== undefined && !String(b.name).trim()) return validation("name: required");
      const next = {
        ...row,
        ...(b.name !== undefined ? { name: String(b.name).trim() } : {}),
        ...(b.base_period !== undefined ? { base_period: b.base_period } : {}),
        ...(b.changes !== undefined ? { changes: b.changes } : {}),
        updated_at: stamp(),
      };
      const evaluated = evaluate(p, next);
      if (evaluated instanceof Response) return evaluated;
      s.scenarios[i] = evaluated;
      return json(evaluated);
    }),
    "scenarios.delete": as((p, a, { params }) => {
      const refused = writer(a);
      if (refused) return refused;
      const s = storeOf(p.company);
      const i = s.scenarios.findIndex((x) => x.id === params.id);
      if (i < 0) return notFound();
      s.scenarios.splice(i, 1);
      return new Response(null, { status: 204 });
    }),

    // Headcount plan ----------------------------------------------------------------------------------
    "headcountPlan.list": as((p, a, { url }) => {
      const refused = restrictedReader(a);
      if (refused) return refused;
      const status = url.searchParams.get("status");
      return list(
        storeOf(p.company).plan.filter((l) => !status || l.status === status),
        url,
      );
    }),
    "headcountPlan.create": as((p, a, { body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const b = (body ?? {}) as Record<string, unknown>;
      const bad = planError(p, b);
      if (bad) return validation(bad);
      const at = stamp();
      const row: Plan = {
        company_id: p.company.id,
        id: next(p, 0x8f80),
        title: str(b.title),
        team_id: (b.team_id as string | null | undefined) ?? null,
        seat_id: (b.seat_id as string | null | undefined) ?? null,
        holder_kind: b.holder_kind as Plan["holder_kind"],
        start_month: b.start_month as string,
        end_month: (b.end_month as string | null | undefined) ?? null,
        monthly_cost_minor: b.monthly_cost_minor as number,
        currency: b.currency as Plan["currency"],
        status: "active",
        note: b.note ? String(b.note) : null,
        ...actor(p, a),
        created_at: at,
        updated_at: at,
      };
      storeOf(p.company).plan.push(row);
      return json(row, 201);
    }),
    "headcountPlan.update": as((p, a, { params, body }) => {
      const refused = writer(a);
      if (refused) return refused;
      const row = storeOf(p.company).plan.find((l) => l.id === params.id);
      if (!row) return notFound();
      const merged = { ...row, ...((body ?? {}) as Record<string, unknown>) };
      const bad = planError(p, merged);
      if (bad) return validation(bad);
      Object.assign(row, {
        title: str(merged.title),
        team_id: merged.team_id ?? null,
        seat_id: merged.seat_id ?? null,
        holder_kind: merged.holder_kind,
        start_month: merged.start_month,
        end_month: merged.end_month ?? null,
        monthly_cost_minor: merged.monthly_cost_minor,
        currency: merged.currency,
        status: merged.status === "archived" ? "archived" : "active",
        note: merged.note ?? null,
        updated_at: stamp(),
      });
      return json(row);
    }),
    "headcountPlan.diff": as((p, a, { url }) => {
      const refused = restrictedReader(a);
      if (refused) return refused;
      const month = url.searchParams.get("month") ?? "";
      if (!MONTH.test(month)) return validation("month: a month key");
      return json(headcountDiff(p, month));
    }),

    // Exports -----------------------------------------------------------------------------------------
    "finance.export": as((p, a, { body }) => {
      const refused = restrictedReader(a);
      if (refused) return refused;
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.format === "xlsx")
        return validation("format: the in-memory API writes CSV only (the API writes XLSX too)");
      if (b.format !== "csv") return validation("format: csv or xlsx");
      const csv = exportCsv(p, String(b.kind), String(b.period_from), String(b.period_to));
      if (csv instanceof Response) return csv;
      return new Response(csv, {
        status: 200,
        headers: {
          "content-type": "text/csv; charset=utf-8",
          "content-disposition": `attachment; filename="${b.kind}-${b.period_from}-${b.period_to}.csv"`,
        },
      });
    }),

    // Price lists and FX rates (M3-04; managers) -----------------------------------------------------------
    "priceLists.list": as((p, a, { url }) => {
      if (!a.manager) return forbidden("managers only");
      const unit = url.searchParams.get("unit");
      const model = url.searchParams.get("model");
      const order = { company: 0, agency: 1, platform: 2 } as const;
      const rows = storeOf(p.company)
        .prices.filter((r) => (!unit || r.unit === unit) && (!model || r.model === model))
        .sort(
          (x, y) =>
            x.unit.localeCompare(y.unit) ||
            (x.model ?? "").localeCompare(y.model ?? "") ||
            order[x.scope] - order[y.scope] ||
            y.effective_from.localeCompare(x.effective_from),
        );
      return list(rows, url);
    }),
    "priceLists.create": as((p, a, { body }) => {
      if (!a.manager) return forbidden("managers only");
      const b = (body ?? {}) as Record<string, unknown>;
      if (!(PRICE_UNITS as readonly unknown[]).includes(b.unit)) return validation("unit");
      if (!isInt(b.price_micros) || b.price_micros < 0)
        return validation("price_micros: 0 or more");
      if (!isCurrency(b.currency)) return validation("currency");
      if (typeof b.effective_from !== "string" || !DATE.test(b.effective_from))
        return validation("effective_from: a date");
      const model = b.model ? str(b.model) : null;
      const s = storeOf(p.company);
      if (
        s.prices.some(
          (r) =>
            r.scope === "company" &&
            r.unit === b.unit &&
            r.model === model &&
            r.effective_from === b.effective_from,
        )
      )
        return conflict("a company price for this unit, model and date exists");
      const row: Price = {
        company_id: p.company.id,
        id: next(p, 0x8c00),
        scope: "company",
        source_price_id: null,
        unit: b.unit as Price["unit"],
        model,
        price_micros: b.price_micros,
        per_units: 1,
        tier_above_tokens: null,
        tier_price_micros: null,
        currency: b.currency,
        effective_from: b.effective_from,
        ...actor(p, a),
        created_at: stamp(),
      };
      s.prices.push(row);
      return json(row, 201);
    }),
    "fxRates.list": as((p, a, { url }) => {
      if (!a.manager) return forbidden("managers only");
      return list(
        [...storeOf(p.company).fx].sort(
          (x, y) =>
            y.period_key.localeCompare(x.period_key) ||
            x.from_currency.localeCompare(y.from_currency) ||
            x.to_currency.localeCompare(y.to_currency),
        ),
        url,
      );
    }),
    "fxRates.put": as((p, a, { params, body }) => {
      if (!a.manager) return forbidden("managers only");
      const period = params.period ?? "";
      if (!MONTH.test(period)) return validation("period: a month key");
      const [from, to] = (params.pair ?? "").split("-");
      if (!isCurrency(from) || !isCurrency(to) || from === to)
        return validation("pair: two different supported currencies, e.g. EUR-USD");
      const b = (body ?? {}) as Record<string, unknown>;
      const rate = typeof b.rate === "number" ? String(b.rate) : b.rate;
      if (typeof rate !== "string" || !DECIMAL.test(rate) || Number(rate) <= 0)
        return validation("rate: a positive decimal");
      const s = storeOf(p.company);
      const at = stamp();
      const had = s.fx.find(
        (r) => r.period_key === period && r.from_currency === from && r.to_currency === to,
      );
      const row: Fx = {
        company_id: p.company.id,
        period_key: period,
        from_currency: from,
        to_currency: to,
        rate,
        source: b.source ? str(b.source) : null,
        ...actor(p, a),
        created_at: had?.created_at ?? at,
        updated_at: at,
      };
      s.fx = [...s.fx.filter((r) => r !== had), row];
      return json(row);
    }),
  };

  // Body rules -----------------------------------------------------------------------------------------

  function grantOf(p: Principal, body: unknown): Grant | Response {
    const b = (body ?? {}) as Record<string, unknown>;
    if (b.relation !== "finance_admin" && b.relation !== "comp_viewer")
      return validation("relation: finance_admin or comp_viewer");
    if (typeof b.membership_id !== "string" || !d.personIn(p, b.membership_id))
      return validation("membership_id: not an active person here");
    if (b.seat_id && !seatIn(p, b.seat_id)) return validation("seat_id: unknown seat");
    if (b.relation === "finance_admin" && b.seat_id)
      return validation("seat_id: finance_admin is a company relation (no seat)");
    if (b.relation === "comp_viewer" && !b.seat_id)
      return validation("seat_id: comp_viewer names its seat");
    return {
      relation: b.relation,
      membership_id: b.membership_id,
      seat_id: (b.seat_id as string | null | undefined) ?? null,
    };
  }

  function compError(p: Principal, b: Record<string, unknown>, create: boolean): string | null {
    if (create && !seatIn(p, b.seat_id)) return "seat_id: unknown seat";
    if (create && !["monthly", "hourly", "contract"].includes(String(b.cadence))) return "cadence";
    if (!isInt(b.amount_minor) || (b.amount_minor as number) < 0) return "amount_minor: 0 or more";
    if (!isCurrency(b.currency)) return "currency";
    if (typeof b.effective_from !== "string" || !DATE.test(b.effective_from))
      return "effective_from: a date";
    if (
      b.effective_to != null &&
      (typeof b.effective_to !== "string" || !DATE.test(b.effective_to))
    )
      return "effective_to: a date";
    if (typeof b.effective_to === "string" && b.effective_to < (b.effective_from as string))
      return "effective_to: before effective_from";
    if (!optUuid(b.membership_id)) return "membership_id";
    if (!categoryIn(p, b.category_id)) return "category_id: unknown category";
    return null;
  }

  function recurringError(p: Principal, b: Record<string, unknown>): string | null {
    if (!str(b.name)) return "name: required";
    if (!isInt(b.amount_minor) || (b.amount_minor as number) < 0) return "amount_minor: 0 or more";
    if (!isCurrency(b.currency)) return "currency";
    if (b.cadence != null && !["monthly", "quarterly", "yearly"].includes(String(b.cadence)))
      return "cadence";
    if (typeof b.start_month !== "string" || !MONTH.test(b.start_month)) return "start_month";
    if (b.end_month != null && (typeof b.end_month !== "string" || !MONTH.test(b.end_month)))
      return "end_month";
    if (typeof b.end_month === "string" && b.end_month < (b.start_month as string))
      return "end_month: before start_month";
    if (b.seat_id && b.team_id) return "seat_id / team_id: one or the other";
    if (b.seat_id && !seatIn(p, b.seat_id)) return "seat_id: unknown seat";
    if (b.team_id && !teamIn(p, b.team_id)) return "team_id: unknown team";
    if (b.kind === "benefit" && !b.seat_id) return "seat_id: a benefit is on a seat";
    if (!categoryIn(p, b.category_id)) return "category_id: unknown category";
    return null;
  }

  function planError(p: Principal, b: Record<string, unknown>): string | null {
    if (!str(b.title)) return "title: required";
    if (b.holder_kind !== "person" && b.holder_kind !== "digital_employee") return "holder_kind";
    if (typeof b.start_month !== "string" || !MONTH.test(b.start_month)) return "start_month";
    if (b.end_month != null && (typeof b.end_month !== "string" || !MONTH.test(b.end_month)))
      return "end_month";
    if (typeof b.end_month === "string" && b.end_month < (b.start_month as string))
      return "end_month: before start_month";
    if (!isInt(b.monthly_cost_minor) || (b.monthly_cost_minor as number) < 0)
      return "monthly_cost_minor: 0 or more";
    if (!isCurrency(b.currency)) return "currency";
    if (b.seat_id && !seatIn(p, b.seat_id)) return "seat_id: unknown seat";
    if (b.team_id && !teamIn(p, b.team_id)) return "team_id: unknown team";
    return null;
  }

  function changeError(p: Principal, c: Record<string, unknown>): string | null {
    if (c.op === "change_budget") {
      const nodes = new Set([
        p.company.id,
        ...d.teamsOf(p.company).map((t) => t.id),
        ...d.seatsOf(p.company).map((s) => s.id),
      ]);
      if (typeof c.node_id !== "string" || !nodes.has(c.node_id))
        return "changes: change_budget names a company, team or seat";
      if (!isInt(c.amount_minor) || (c.amount_minor as number) < 0) return "changes: amount_minor";
      return null;
    }
    if (!["add_seat", "remove_seat", "change_holder"].includes(String(c.op))) return "changes: op";
    if (!str(c.seat_template)) return "changes: seat_template";
    if (!isInt(c.count) || (c.count as number) < 1 || (c.count as number) > 1000)
      return "changes: count 1 to 1000";
    if (c.holder_kind !== "person" && c.holder_kind !== "digital_employee")
      return "changes: holder_kind";
    if (
      c.op !== "remove_seat" &&
      (!isInt(c.assumed_cost_minor) || (c.assumed_cost_minor as number) < 0)
    )
      return "changes: assumed_cost_minor";
    if (
      c.op === "change_holder" &&
      c.from_holder_kind !== "person" &&
      c.from_holder_kind !== "digital_employee"
    )
      return "changes: from_holder_kind";
    return null;
  }
}

const same = (x: Grant, y: Grant) =>
  x.relation === y.relation && x.membership_id === y.membership_id && x.seat_id === y.seat_id;

/** A rate the period has not got (thrown inside a view; the handler answers 422). */
class MissingRate extends Error {
  override name = "MissingRate";
}

/** The contract's change (snake case, minor units as numbers) as the engine's. */
function toEngine(c: ScenarioJson): ScenarioChange {
  const kpi = (deltas: { metric_key: string; delta: number }[] | undefined) =>
    deltas?.map((x) => ({ metricKey: x.metric_key, delta: x.delta }));
  switch (c.op) {
    case "add_seat":
      return {
        op: "add_seat",
        seatTemplate: c.seat_template,
        holderKind: c.holder_kind,
        count: c.count,
        assumedCostMinor: BigInt(c.assumed_cost_minor),
        ...(c.assumed_kpi_delta ? { assumedKpiDelta: kpi(c.assumed_kpi_delta) ?? [] } : {}),
      };
    case "remove_seat":
      return {
        op: "remove_seat",
        seatTemplate: c.seat_template,
        holderKind: c.holder_kind,
        count: c.count,
        ...(c.assumed_kpi_delta ? { assumedKpiDelta: kpi(c.assumed_kpi_delta) ?? [] } : {}),
      };
    case "change_holder":
      return {
        op: "change_holder",
        seatTemplate: c.seat_template,
        fromHolderKind: c.from_holder_kind,
        holderKind: c.holder_kind,
        count: c.count,
        assumedCostMinor: BigInt(c.assumed_cost_minor),
        ...(c.assumed_kpi_delta ? { assumedKpiDelta: kpi(c.assumed_kpi_delta) ?? [] } : {}),
      };
    case "change_budget":
      return { op: "change_budget", nodeId: c.node_id, amountMinor: BigInt(c.amount_minor) };
  }
}
